import { useCallback, useEffect, useState } from 'react';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormSetError,
} from 'react-hook-form';
import {
  BaseTransaction,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  TransactionWarning,
  TransactionWarningActionBody,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useApplyInternalTransactionWarningActionMutation,
  useCheckInternalTransactionMutation,
  useCreateInternalTransactionMutation,
  useLazyGetInternalListQuery,
  useLazyGetInternalAmlQuery,
  useLazyGetInternalTransactionByIdQuery,
  useLazyGetInternalTransactionWarningsQuery,
  useUpdateInternalTransactionMutation,
  useLazyGetSenderClientsOfficiasQuery,
  useLazyGetRecipientClientsOfficiasQuery,
  useResetInternalTransactionWarningActionMutation,
  useLazyGetInternalTransactionWarningQuery,
  useLazyGetInternalTransactionWarningsInfoQuery,
} from '../store/services';
import StatusesUrl from '../../../types/statuses-url';
import useRetry from '../../outgoing/helpers/use-retry';
import { useLazyGetStatusesTransactionByIdQuery } from '../../../store/statuses/services';
import {
  CheckTransaction,
  CheckTransactionStatus,
} from '../../../types/check-transaction';
import { setWarnings } from '../store/slices/transaction';
import { UpdateInternalTransaction } from '../types';

const useRequests = ({
  transactionId,
  warnings,
  transaction,
  activePermissions,
  setCheckError,
  fields,
  setError,
  setErrorMessage,
  setStatusChangeError,
  getValues,
  errors,
  isDirty,
  panelIsOpen,
  setStatusCheckError,
  setWarningActionError,
}: {
  transactionId?: string;
  warnings?: TransactionWarning[];
  transaction?: BaseTransaction;
  activePermissions?: PermissionEnum[];
  setCheckError: (data: string) => void;
  fields: any;
  setError: UseFormSetError<any>;
  setErrorMessage: (data: string) => void;
  setStatusChangeError: (data: string) => void;
  getValues: UseFormGetValues<any>;
  errors: FieldErrors<any>;
  isDirty?: boolean;
  panelIsOpen: boolean;
  setStatusCheckError: (data: string) => void;
  setWarningActionError: (
    data: { warningId: string; error: string } | undefined,
  ) => void;
}) => {
  const dispatch = useAppDispatch();

  const [feeValue, setFeeValue] = useState<number | undefined>(undefined);
  const [checkStatus, setCheckStatus] = useState<
    CheckTransactionStatus | undefined
  >(undefined);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [transferDateWithTime, setTransferDateWithTime] = useState(false);
  const [postingDateWithTime, setPostingDateWithTime] = useState(false);

  const [getInternalTransactionById, { isLoading, isUninitialized }] =
    useLazyGetInternalTransactionByIdQuery();
  const [update, { isLoading: editIsLoading, isError: updateIsError }] =
    useUpdateInternalTransactionMutation();
  const [create, { isSuccess: createSuccess, isLoading: createInProgress }] =
    useCreateInternalTransactionMutation();
  const [getTransactionsList] = useLazyGetInternalListQuery();
  const [
    getInternalTransactionWarnings,
    { isLoading: isWarningLoading, isFetching: isWarningFetching },
  ] = useLazyGetInternalTransactionWarningsQuery();
  const [
    getInternalTransactionWarningsInfo,
    { isLoading: isWarningInfoLoading },
  ] = useLazyGetInternalTransactionWarningsInfoQuery();
  const [
    getInternalTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetInternalTransactionWarningQuery();
  const [checkPayment, { isLoading: checkInProgress }] =
    useCheckInternalTransactionMutation();
  const [getInternalTransactionByIdAml, { isLoading: isLoadingAml }] =
    useLazyGetInternalAmlQuery();
  const [
    applyInternalTransactionWarning,
    { isLoading: applyTransactionsWarningIsLoading },
  ] = useApplyInternalTransactionWarningActionMutation();
  const [getSenderCompanyOfficials] = useLazyGetSenderClientsOfficiasQuery();
  const [getRecipientCompanyOfficials] =
    useLazyGetRecipientClientsOfficiasQuery();
  const [
    resetInternalTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetInternalTransactionWarningActionMutation();

  const [
    getStatusesTransactionById,
    {
      isLoading: isLoadingStatuses,
      isFetching: isFetchingStatuses,
      isUninitialized: isUninitializedStatuses,
    },
  ] = useLazyGetStatusesTransactionByIdQuery();

  const getInternalTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    setSelectedWarningId(warningId);

    await getInternalTransactionWarningById({
      id,
      warningId,
    });
  };

  const onResetTransactionWarningAction = async (warningId: string) => {
    const response = (await resetInternalTransactionWarning({
      warningId,
      transactionId: transactionId as string,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings?.map((item) => {
        const result = { ...item };

        if (result.id === response?.data?.data?.id) {
          result.action = response.data.data.action;
          result.matchStatus = response.data.data.matchStatus;
        }

        return result;
      });

      getInternalTransactionWarningsInfo(transactionId as string);

      dispatch(setWarnings(nextWarnings as TransactionWarning[]));
    }
  };

  const onApplyTransactionAction = async ({
    body,
    warningId,
  }: {
    body: TransactionWarningActionBody;
    warningId: string;
  }) => {
    setWarningActionError(undefined);

    const response = (await applyInternalTransactionWarning({
      transactionId: transactionId as string,
      body,
      warningId,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings
        ?.map((item) => {
          const result = { ...item };

          if (result.id === response?.data?.data?.id) {
            result.action = response.data.data.action;
            result.matchStatus = response.data.data.matchStatus;
          }

          return result;
        })
        ?.filter((item) => !item.action);

      getInternalTransactionWarningsInfo(transactionId as string);

      if (!nextWarnings?.length) {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.INTERNAL,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    } else if (response?.error) {
      setWarningActionError({
        warningId,
        error: response?.error?.data?.message as string,
      });
    }
  };

  const { getTransaction, onClearTimer, internalError } = useRetry({
    getTransactionFunction: getInternalTransactionById,
    id: transactionId as string,
    isRetry: true,
    getStatusesFunction: getStatusesTransactionById,
    getTransactionWarningsFunction: getInternalTransactionWarnings,
    getTransactionWarningsInfoFunction: getInternalTransactionWarningsInfo,
    chatPermission: activePermissions?.includes(PermissionEnum.CHANNELS_LIST),
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_INTERNAL_EDIT_ACTION,
    ),
    statusUrl: StatusesUrl.INTERNAL,
    warningsInfoPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_INTERNAL_AML_LIST,
    ),
  });

  const abortController = (() => {
    let controller = new AbortController();
    let controllerSignal = controller.signal;

    const abortPendingRequest = () => {
      controller.abort();
      // eslint-disable-next-line react-hooks/immutability
      controller = new AbortController();
      controllerSignal = controller.signal;
    };

    const getSignalToken = () => controllerSignal;

    return {
      abortPendingRequest,
      getSignalToken,
    };
  })();

  const checkPaymentAction = useCallback(async () => {
    abortController.abortPendingRequest();

    setCheckError('');
    setStatusCheckError('');

    const body: CheckTransaction = {
      sum: Number(fields.sum),
      senderAccountNumber: fields.senderAccountNumber,
      recipientAccountNumber: fields.recipientAccountNumber,
      currencyId: fields.currencyId,
      info: fields.info,
    };

    if (fields?.caspId) {
      body.caspId = fields.caspId;
    }

    const response = (await checkPayment({
      body,
      abortController,
    })) as {
      data?: any;
      error?: { data: { message?: string }; message?: string } | ResponseError;
    };

    if ('data' in response) {
      setFeeValue(response.data.data.totalFee);
      setCheckStatus(response.data.data.status);

      if (response?.data?.data?.errorMessage) {
        setStatusCheckError(response?.data?.data?.errorMessage);
      }

      if (response.data.data.status === CheckTransactionStatus.GREEN) {
        setSubmitDisabled(false);
      }
    } else {
      setSubmitDisabled(true);

      if (response && response.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCheckError,
        });
      }
    }
  }, [
    checkPayment,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields.caspId,
    fields.info,
  ]);

  const onSubmit = async () => {
    let response;

    if (!Object.keys(errors).length) {
      setStatusChangeError('');
      setErrorMessage('');
      const values = getValues();

      const body: UpdateInternalTransaction = {
        documentNumber: values.documentNumber || undefined,
        info: values.info || undefined,
      };

      if (values?.depositDateOfDestinationAccount) {
        const date = new Date(values.depositDateOfDestinationAccount);

        if (postingDateWithTime) {
          body.depositDateOfDestinationAccount = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);
          body.depositDateOfDestinationAccount = date.toISOString();
        }
      }

      response = (await update({
        body,
        id: transactionId as string,
      })) as {
        data?: BaseTransaction;
        error: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.INTERNAL,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    }

    return response;
  };

  const getInternalTransactionByIdAmlAction = () => {
    getInternalTransactionByIdAml(transactionId as string);
  };

  useEffect(() => {
    if (
      !transactionId &&
      isDirty &&
      fields.sum.toString() &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      fields.currencyId &&
      fields.info
    ) {
      checkPaymentAction();
    }
  }, [
    transactionId,
    isDirty,
    checkPaymentAction,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields.info,
  ]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();

      setPostingDateWithTime(true);
      setTransferDateWithTime(true);
    }
  }, [panelIsOpen, transactionId]);

  useEffect(() => {
    if (
      transaction &&
      transaction.senderAccount &&
      transaction.senderAccount?.organization?.type === 'CORPORATE'
    ) {
      getSenderCompanyOfficials(transaction.senderAccount.organizationId);
    }
  }, [transaction]);

  useEffect(() => {
    if (
      transaction &&
      transaction.recipientAccount &&
      transaction.recipientAccount?.organization?.type === 'CORPORATE'
    ) {
      getRecipientCompanyOfficials(transaction.recipientAccount.organizationId);
    }
  }, [transaction]);

  return {
    feeValue,
    setFeeValue,
    checkStatus,
    setCheckStatus,
    submitDisabled,
    setSubmitDisabled,
    isLoading,
    isUninitialized,
    editIsLoading,
    updateIsError,
    create,
    createSuccess,
    createInProgress,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    checkInProgress,
    isLoadingAml,
    applyTransactionsWarningIsLoading,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    onApplyTransactionAction,
    onClearTimer,
    internalError,
    onSubmit,
    getInternalTransactionByIdAmlAction,
    getStatusesTransactionById,
    getTransaction,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getInternalTransactionWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    isWarningInfoLoading,
    selectedWarningId,
    setPostingDateWithTime,
    setTransferDateWithTime,
    postingDateWithTime,
    transferDateWithTime,
  };
};

export default useRequests;
